import { useCallback, useEffect, useState } from "react";
import { getSortString } from "../../utils/tableUtils";
import usePagination from "./usePagination";
import { isEqual } from "lodash";

export const useTableData = (
  fetchData,
  initialTableState,
  extraExternalFilters = null
) => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const paginationState = usePagination({
    totalCount,
    initialPage: initialTableState?.page,
    initialPageSize: initialTableState?.pageSize
  });

  const [selectedRowIds, setSelectedRowIds] = useState({});
  const [rowState, setRowState] = useState(null);

  const [tableState, setTableState] = useState({
    pageNumber: initialTableState?.page ?? paginationState.page,
    pageSize: initialTableState?.pageSize ?? paginationState.pageSize,
    filters: initialTableState?.filters ?? [],
    externalFilters: initialTableState?.externalFilters ?? {}, // Filters outside of table
    sortBy: initialTableState?.sortBy ?? []
  });

  const dataKey = initialTableState?.dataKey;

  let { filters, externalFilters, sortBy, pageNumber, pageSize } = tableState;

  const updateTableState = (newState, resetPagination) => {
    if (resetPagination) {
      paginationState.setPage(1);
    }
    setTableState(oldState => ({
      ...oldState,
      pageNumber: resetPagination ? 1 : oldState.pageNumber,
      ...newState
    }));
  };

  const fetchTableData = () => {
    setIsFetching(true);
    let queryFilters = filters.reduce(
      (currentFilters, { id, value }) => ({
        ...currentFilters,
        [id]: value
      }),
      {}
    );
    queryFilters = !extraExternalFilters
      ? { ...queryFilters, ...externalFilters }
      : { ...queryFilters, ...extraExternalFilters };
    fetchData({
      page: {
        number: pageNumber,
        size: pageSize
      },
      filters: queryFilters,
      sortBy: getSortString(sortBy[0])
    }).then(results => {
      let resultData = dataKey ? results.data[dataKey] : results.data;
      setData(resultData);
      setTotalCount(results.totalCount);
      setIsFetching(false);
    });
  };

  useEffect(() => {
    fetchTableData();
  }, [tableState]);

  const setPage = newPage => {
    paginationState.setPage(newPage);
    updateTableState({ pageNumber: newPage });
  };

  const setFetchSize = newFetchSize => {
    paginationState.setFetchSize(newFetchSize);
    updateTableState({ pageSize: newFetchSize });
  };

  const onStateChange = useCallback(
    state => {
      if (state.filters && !isEqual(state.filters, filters)) {
        updateTableState({ filters: state.filters }, true);
      }
      if (state.sortBy && !isEqual(state.sortBy, sortBy)) {
        updateTableState({ sortBy: state.sortBy }, true);
      }
      if (
        state.selectedRowIds &&
        !isEqual(state.selectedRowIds, selectedRowIds)
      ) {
        setSelectedRowIds(state.selectedRowIds);
      }
      if (state.rowState && !isEqual(rowState, state.rowState)) {
        setRowState(state.rowState);
      }
    },
    [filters, sortBy, rowState, selectedRowIds]
  );

  const onApplyExternalFilters = useCallback(newFilters => {
    const newExternalFilters = {
      ...externalFilters,
      ...newFilters
    };
    if (!isEqual(newExternalFilters, externalFilters)) {
      externalFilters = newExternalFilters;
      updateTableState({ externalFilters }, true);
    }
  });

  return {
    data,
    externalFilters,
    isFetching,
    paginationState: { ...paginationState, setPage, setFetchSize },
    onStateChange,
    onApplyExternalFilters,
    refreshData: fetchTableData,
    selectedRowIds,
    rowState
  };
};
